// Where a first-contact tip's card sits relative to its anchor (components/TipLayer.jsx).

export const TIP_WIDTH = 280;
/** The narrowest a beside card gets to fit next to its anchor. */
const MIN_BESIDE_WIDTH = 180;
const GAP = 10;
const EDGE = 8;
/** Share of the viewport height above which an anchor gets the card inside it. */
const LARGE_ANCHOR = 0.5;
/** How far inside a large anchor the card sits, clear of a heading or label row. */
const INSIDE_OFFSET = 40;
/** The arrow's closest approach to a corner of the card. */
const ARROW_INSET = 16;

const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);

/**
 * Which side of the anchor a beside card takes and how wide it is: the right, else the left,
 * narrowed to the wider side's room when neither fits it whole; null when that is under
 * MIN_BESIDE_WIDTH. Widths only, so it is known before the card is measured.
 */
function besideSlot(rect, vw) {
  const w = Math.min(TIP_WIDTH, vw - 2 * EDGE);
  const roomRight = vw - EDGE - (rect.left + rect.width + GAP);
  const roomLeft = rect.left - GAP - EDGE;
  if (roomRight >= w) return { side: "right", width: w };
  if (roomLeft >= w) return { side: "left", width: w };
  const width = Math.max(roomRight, roomLeft);
  if (width < MIN_BESIDE_WIDTH) return null;
  return { side: roomRight >= roomLeft ? "right" : "left", width };
}

/** Whether a `beside` tip's card has a place next to an anchor in a viewport this wide. */
export const fitsBeside = (rect, vw) => besideSlot(rect, vw) !== null;

/** The card in its beside slot, vertically centred on the anchor; null when there is none. */
function placeBeside(rect, cardH, vw, vh) {
  const slot = besideSlot(rect, vw);
  if (!slot) return null;
  const { side, width } = slot;
  const x = side === "right" ? rect.left + rect.width + GAP : rect.left - GAP - width;
  const mid = rect.top + rect.height / 2;
  const top = clamp(mid - cardH / 2, EDGE, vh - cardH - EDGE);
  const arrowY = clamp(mid - top, ARROW_INSET, cardH - ARROW_INSET);
  return { top, left: x, width, side, arrowY };
}

/**
 * Where the card goes for an anchor rect: below it, else above it, else inside its top edge;
 * an `above` tip tries above first, for an anchor with related controls below it. An anchor
 * that fills most of the screen (the timeline) always gets the card inside, clear of its heading
 * row, so the card does not cover the controls around it. Horizontally centred on the anchor and
 * kept on screen.
 *
 * A `beside` tip goes to the anchor's right, else its left (see besideSlot), never below, above
 * or inside: its anchor is a dialog the card must not cover. With no room on either side there
 * is no place — null — and the tip waits (hooks/useOnboardingTips.js asks fitsBeside first).
 *
 * @param {{top: number, left: number, width: number, height: number}} rect  viewport rect
 * @param {number} cardH  the card's rendered height
 * @param {{vw: number, vh: number, beside?: boolean, above?: boolean}} opts  viewport size, and
 *   the tip's `beside` and `above`
 * @returns {{top: number, left: number, width: number,
 *   side: "below"|"above"|"inside"|"right"|"left", arrowX?: number, arrowY?: number}|null}
 */
export function placeTip(rect, cardH, { vw, vh, beside = false, above: aboveFirst = false }) {
  if (beside) return placeBeside(rect, cardH, vw, vh);
  const w = Math.min(TIP_WIDTH, vw - 2 * EDGE);
  // Never further left than the anchor itself (when it fits), so a tip on a control beside the
  // navigation rail does not cover the rail.
  const minLeft = Math.max(EDGE, Math.min(rect.left, vw - w - EDGE));
  const left = Math.min(Math.max(rect.left + rect.width / 2 - w / 2, minLeft), vw - w - EDGE);
  const below = rect.top + rect.height + GAP;
  const above = rect.top - GAP - cardH;
  const fitsBelow = below + cardH <= vh - EDGE;
  const fitsAbove = above >= EDGE;
  let top;
  let side;
  if (rect.height > vh * LARGE_ANCHOR) {
    top = Math.min(Math.max(rect.top + INSIDE_OFFSET, EDGE), vh - cardH - EDGE);
    side = "inside";
  } else if (aboveFirst && fitsAbove) {
    top = above;
    side = "above";
  } else if (fitsBelow) {
    top = below;
    side = "below";
  } else if (fitsAbove) {
    top = above;
    side = "above";
  } else {
    top = Math.max(rect.top + INSIDE_OFFSET, EDGE);
    side = "inside";
  }
  const arrowX = clamp(rect.left + rect.width / 2 - left, ARROW_INSET, w - ARROW_INSET);
  return { top, left, width: w, side, arrowX };
}
